import React from "react";
import { Pie, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/* =========================
   ORANGE DASHBOARD PALETTE
   ========================= */
const ORANGE_SHADES = [
  "#FFF3E0",
  "#FFE0B2",
  "#FFCC80",
  "#FFB74D",
  "#FFA726",
  "#FB8C00",
  "#F57C00",
  "#EF6C00",
  "#E65100",
];

/* =========================
   STATUS NORMALIZER
   (MUST MATCH METRIC CARDS)
   ========================= */
const normalizeStatus = (status) => {
  if (!status) return "Applied";

  const s = status.toUpperCase();

  if (["SHORTLISTED", "INTERVIEW", "INTERVIEWED"].includes(s))
    return "Shortlisted";

  if (["SUCCESSFUL", "HIRED", "OFFERED"].includes(s))
    return "Successful";

  if (["UNSUCCESSFUL", "REJECTED", "FAILED"].includes(s))
    return "Unsuccessful";

  return "Applied";
};

const ApplicantsCharts = ({ applicants, type }) => {
  if (!applicants || applicants.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center mt-10">
        No data to display
      </p>
    );
  }

  let data = {};
  let options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: { enabled: true },
    },
  };

  /* =========================
     APPLICANTS BY SOURCE
     ========================= */
  if (type === "source") {
    const sources = applicants.reduce((acc, a) => {
      const src = a.source || "Other Sources";
      acc[src] = (acc[src] || 0) + 1;
      return acc;
    }, {});

    data = {
      labels: Object.keys(sources),
      datasets: [
        {
          label: "Applicants by Source",
          data: Object.values(sources),
          backgroundColor: ORANGE_SHADES.slice(
            0,
            Object.keys(sources).length
          ),
          borderWidth: 1,
        },
      ],
    };

    return <Pie data={data} options={options} />;
  }

  /* =========================
     APPLICANTS PER JOB
     ========================= */
  if (type === "jobs") {
    const jobsCount = applicants.reduce((acc, a) => {
      const jobName = a.job?.vacancy || "Unassigned Job";
      acc[jobName] = (acc[jobName] || 0) + 1;
      return acc;
    }, {});

    data = {
      labels: Object.keys(jobsCount),
      datasets: [
        {
          label: "Applicants per Job",
          data: Object.values(jobsCount),
          backgroundColor: "#FB8C00",
          borderRadius: 6,
          barThickness: 30,
        },
      ],
    };

    options = {
      ...options,
      scales: {
        y: { beginAtZero: true },
      },
    };

    return <Bar data={data} options={options} />;
  }

  /* =========================
     APPLICANT STATUS
     (MATCHES KPI CARDS)
     ========================= */
  if (type === "status") {
    const statusCount = applicants.reduce((acc, a) => {
      const status = normalizeStatus(a.status);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    data = {
      labels: Object.keys(statusCount),
      datasets: [
        {
          label: "Applicants by Status",
          data: Object.values(statusCount),
          borderColor: "#F57C00",
          backgroundColor: "rgba(245, 124, 0, 0.25)",
          tension: 0.4,
          fill: true,
          pointRadius: 5,
          pointBackgroundColor: "#E65100",
        },
      ],
    };

    options = {
      ...options,
      scales: {
        y: { beginAtZero: true },
      },
    };

    return <Line data={data} options={options} />;
  }

  return <p>Chart type not supported</p>;
};

export default ApplicantsCharts;


